import React, { useMemo, useState, useEffect } from 'react'
import { Link, Redirect } from 'react-router-dom'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import startCase from 'lodash/startCase'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'

import {
    HydraTable,
    Heading,
    TextFilter,
    SelectFilter,
    DateRangeFilter,
    StyledCheckbox,
    TimeAgo,
} from '@peracto/peracto-ui'

import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'

import {
    DEFAULT_PRODUCT_AVAILABILITY_VALUES,
    DEFAULT_PRODUCT_STATUS_VALUES,
} from '../../Products/util'

const ProductImage = ({ resources, name }) => {
    let img = null

    resources?.forEach(resource => {
        if (
            !img ||
            (resource.productResource && resource.productResource?.displayOrder < img.displayOrder)
        ) {
            img = resource.productResource
        }
    })

    return !!img?.location ? (
        <img
            src={img?.location}
            title={name}
            alt={name}
            style={{ height: '40px', width: '40px' }}
        />
    ) : (
        <FontAwesomeIcon
            icon={faImage}
            style={{ width: '40px', height: '40px', color: '#606060' }}
        />
    )
}

const VariantsList = () => {
    const [redirect, setRedirect] = useState()
    const { client } = useClient()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const { products } = config.get('features', {})

    useEffect(() => {
        if (!products?.productVariants) {
            setRedirect(`/`)
        }
    }, [products])

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => [
        {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
                <StyledCheckbox
                    {...row.getToggleRowSelectedProps()}
                    data-testid={`row-${row.index}-checkbox`}
                />
            ),
            selectable: false,
        },
        {
            accessor: 'resources',
            Cell: row => {
                const splitProductIRI = row.row.original.product['@id'].split('/')
                const productId = splitProductIRI[splitProductIRI.length - 1]

                return (
                    <Link
                        to={`/products/${productId}/variants/${row.row.original.originId}?to=variants`}
                        data-testid={`row-${row.row.index}-image`}
                    >
                        <ProductImage resources={row.cell.value} name={row.row.values.name} />
                    </Link>
                )
            },

            selectable: false,
        },
        {
            Header: 'SKU',
            accessor: 'sku',
            Filter: props => <TextFilter label="SKU" {...props} />,
            Cell: row => {
                const splitProductIRI = row.row.original.product['@id'].split('/')
                const productId = splitProductIRI[splitProductIRI.length - 1]

                return (
                    <Link
                        to={`/products/${productId}/variants/${row.row.original.originId}?to=variants`}
                        data-testid={`row-${row.row.index}-sku`}
                    >
                        {row.cell.value}
                    </Link>
                )
            },
        },
        {
            Header: 'Product Name',
            accessor: 'product.name',
            Cell: row => row.cell.value || '-',
        },
        {
            Header: 'Product SKU',
            accessor: 'product.sku',
            Cell: row => row.cell.value || '-',
        },
        {
            Header: 'Availability',
            accessor: 'availability',
            Filter: props => (
                <SelectFilter
                    label="Availability"
                    options={[
                        { value: '', label: 'Please select...' },
                        ...DEFAULT_PRODUCT_AVAILABILITY_VALUES,
                    ]}
                    {...props}
                />
            ),
            Cell: ({ row, cell: { value } }) => startCase(value),
            selectable: false,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sortType: 'basic',
            Filter: props => (
                <SelectFilter
                    label="Status"
                    options={[
                        { value: '', label: 'Please select...' },
                        ...DEFAULT_PRODUCT_STATUS_VALUES,
                    ]}
                    {...props}
                />
            ),
            Cell: ({ cell: { value } }) => (
                <span
                    className={classNames('badge', 'badge-pill', {
                        'badge-info': value === 'draft',
                        'badge-success': value === 'active',
                        'badge-danger': value === 'deleted',
                    })}
                >
                    {startCase(value)}
                </span>
            ),
            selectable: false,
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedAt',
            sortType: 'basic',
            Filter: props => <DateRangeFilter label="Last Modified" {...props} />,
            Cell: ({ cell: { value } }) =>
                value ? <TimeAgo title={formatDate(new Date(value))} date={value} /> : '-',
            selectable: false,
        },
    ])

    const onFetchData = options => client(GET_LIST, 'product-variants', options)

    return (
        <>
            {redirect && <Redirect to={redirect} />}
            <Helmet>
                <title>Product Variants | Peracto</title>
            </Helmet>
            <Heading name="Product Variants" />

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={columns}
                                onFetchData={onFetchData}
                                defaultFilters={{
                                    sku: '',
                                    status: '',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default VariantsList
